import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Icon, IconName } from './Icon';

export interface TooltipProps {
  content: ReactNode;
  children?: ReactNode;
}

const CLOSE_DELAY_MS = 120;

// A hover/focus tooltip rendered into document.body, so it is never clipped by
// a scrolling table. It stays open while the pointer is over the tooltip
// itself, because some tooltips here hold a link or a copy button.
export function Tooltip({ content, children }: TooltipProps) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const closeTimer = useRef<number>();

  const open = (e: React.SyntheticEvent<HTMLElement>) => {
    window.clearTimeout(closeTimer.current);
    setAnchor(e.currentTarget.getBoundingClientRect());
  };
  const keepOpen = () => window.clearTimeout(closeTimer.current);
  const close = () => {
    closeTimer.current = window.setTimeout(() => setAnchor(null), CLOSE_DELAY_MS);
  };

  // A fixed-position tooltip would drift away from its trigger on scroll.
  useEffect(() => {
    if (!anchor) return undefined;
    const hide = () => setAnchor(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [anchor]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const hasContent = content !== undefined && content !== null && content !== '';

  return (
    <span className="ec-tooltip-trigger" onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close}>
      {children}
      {anchor && hasContent && createPortal(
        <div
          role="tooltip"
          className="ec-tooltip"
          style={{ left: anchor.left + anchor.width / 2, top: anchor.top }}
          onMouseEnter={keepOpen}
          onMouseLeave={close}
        >
          {content}
        </div>,
        document.body
      )}
    </span>
  );
}

// The text a tooltip trigger carries as an attribute, so tests and the
// browser's inspector can read it without hovering.
function tooltipText(content: ReactNode): string {
  return typeof content === 'string' || typeof content === 'number' ? String(content) : '';
}

export interface InfoIconProps {
  children?: ReactNode;
  className?: string;
  icon?: IconName;
  testId?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

// An icon-only button that shows its children as a tooltip.
export function InfoIcon({ children, className, icon = 'semantic-info', testId, onClick }: InfoIconProps) {
  return (
    <Tooltip content={children}>
      <button
        type="button"
        data-testid={testId}
        data-tooltip={tooltipText(children)}
        aria-label={tooltipText(children) || 'More information'}
        className={classNames('ec-info-icon', className)}
        onClick={onClick}
      >
        <Icon name={icon} />
      </button>
    </Tooltip>
  );
}
